import React, { useState, useCallback } from 'react';

import {
  BotDetector,
  CSRFProtection,
  SecurityMonitor,
  securityValidators,
} from '../../utils/security';
import {
  apiRateLimit,
  generalRateLimit,
  isRateLimited,
} from './security.services';
import { SecurityContext } from './securityContext';
import { SecurityContextType, SecurityState } from './security.types';

/**
 * @file Provides a centralized React Context for managing application-wide security concerns.
 * This includes bot detection, CSRF protection, rate limiting, and input validation.
 *
 * @module SecurityContext
 */

/**
 * Provides the Security Context to its children.
 * It initializes security state, including bot detection and CSRF token generation,
 * and exposes security functions to the rest of the application.
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to render.
 * @returns {JSX.Element} The SecurityProvider component.
 */
export const SecurityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  /**
   * Initializes and holds the security state.
   * This function runs only once on component mount.
   */
  const [security] = useState<SecurityState>(() => {
    const userAgent = navigator.userAgent;
    const isBot = BotDetector.isBot(userAgent);

    // Log an event if a bot is detected.
    if (isBot) {
      SecurityMonitor.logSecurityEvent('BOT_DETECTED', { userAgent });
    }

    // Ensure a CSRF token is always available.
    const csrfToken =
      CSRFProtection.getToken() || CSRFProtection.generateToken();

    return {
      isBot,
      csrfToken,
    };
  });

  /**
   * Memoized function to check API rate limits.
   * Logs a security event if an action is blocked.
   */
  const isApiRateLimited = useCallback(
    (key: string) => isRateLimited(apiRateLimit, key, 'API_RATE_LIMITED'),
    []
  );

  /**
   * Memoized function to check general rate limits.
   * Logs a security event if an action is blocked.
   */
  const isGeneralRateLimited = useCallback(
    (key: string) =>
      isRateLimited(generalRateLimit, key, 'GENERAL_RATE_LIMITED'),
    []
  );

  /**
   * The context value, combining state and memoized functions.
   */
  const value: SecurityContextType = {
    security,
    isApiRateLimited,
    isGeneralRateLimited,
    sanitizeInput: securityValidators.sanitizeInput,
    validateEmail: securityValidators.isValidEmail,
    detectXSS: securityValidators.detectXSS,
    logSecurityEvent: SecurityMonitor.logSecurityEvent,
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};
